import { useRef, useState } from "preact/hooks";
import { useRoute } from "preact-iso";
import { KeyIcon, PhotoIcon, UsersIcon } from "@heroicons/react/24/outline";
import { api } from "../lib/api-client";
import { adminHref } from "../lib/platform-admin";
import { useAsyncData } from "../lib/use-async-data";
import { canManageProject } from "../lib/permissions";
import { OwnerBanner } from "../components/owner-banner";
import { ProjectMetadataSection } from "../components/project/project-metadata-section";
import { DeployHookSection } from "../components/project/deploy-hook-section";
import { FrontmatterTemplateSection } from "../components/project/frontmatter-template-section";
import { ContentListSection } from "../components/project/content-list-section";
import { DangerZoneSection } from "../components/project/danger-zone-section";
import { ImageModal } from "../components/image-modal";
import {
  Alert,
  Button,
  PageHeader,
  SkeletonList,
  SkeletonPageHeader,
} from "../components/ui";
import type { ProjectDto } from "@shared/types";

export function ProjectDetail() {
  const { params } = useRoute();
  const projectId = params.projectId!;
  const [imagesOpen, setImagesOpen] = useState(false);

  const { data, error, setData } = useAsyncData(
    () =>
      Promise.all([
        api.getProject(projectId),
        api.listContents(projectId),
      ]).then(([p, list]) => ({ project: p, contents: list.items })),
    [projectId],
  );
  const project = data?.project ?? null;
  const contents = data?.contents ?? null;
  const currentProjectIdRef = useRef<string | null>(null);
  currentProjectIdRef.current = project?.id ?? null;
  // Child settings sections push updated project rows back up (deploy hook,
  // template) — patch just the project slice of the async result.
  const setProject = (p: ProjectDto) =>
    setData((d) =>
      d && currentProjectIdRef.current === p.id ? { ...d, project: p } : d,
    );

  // Destructive / settings sections are owner or platform-admin only. The
  // server enforces this independently; the UI just avoids tempting members
  // with controls they can't use.
  const canManage = canManageProject(project?.viewerRole);

  // While the project / content list load, show a header + list skeleton so the
  // title and sections don't pop in from a "..." placeholder. Errors fall
  // through to the main return below (which surfaces the Alert).
  if (!project && !error) {
    return (
      <section class="space-y-10">
        <SkeletonPageHeader actions={1} />
        <SkeletonList count={3} />
      </section>
    );
  }

  return (
    <section class="space-y-10">
      <PageHeader
        title={project?.name ?? "..."}
        description={<span class="font-mono">/{project?.slug ?? ""}</span>}
        actions={
          <div class="flex flex-wrap items-center gap-2">
            <Button
              as="a"
              href={adminHref(`/projects/${projectId}/members`)}
              variant="secondary"
              iconLeft={<UsersIcon class="h-4 w-4" />}
            >
              メンバー
            </Button>
            {canManage && (
              <Button
                as="a"
                href={adminHref(`/projects/${projectId}/keys`)}
                variant="secondary"
                iconLeft={<KeyIcon class="h-4 w-4" />}
              >
                APIキー管理
              </Button>
            )}
            <Button
              variant="secondary"
              iconLeft={<PhotoIcon class="h-4 w-4" />}
              onClick={() => setImagesOpen(true)}
            >
              画像
            </Button>
          </div>
        }
      />

      {error && <Alert tone="danger">{error}</Alert>}

      <OwnerBanner project={project} />

      <ImageModal
        projectIdOrSlug={projectId}
        open={imagesOpen}
        onClose={() => setImagesOpen(false)}
      />

      {project && (
        <>
          {canManage && (
            <>
              <ProjectMetadataSection
                key={`metadata-${project.id}`}
                project={project}
                onProjectUpdate={setProject}
              />
              <DeployHookSection
                key={`deploy-${project.id}`}
                project={project}
                onProjectUpdate={setProject}
              />
              <FrontmatterTemplateSection
                key={`template-${project.id}`}
                project={project}
                onProjectUpdate={setProject}
              />
            </>
          )}
          <ContentListSection
            key={`contents-${project.id}`}
            projectId={projectId}
            contents={contents}
          />
          {canManage && (
            <DangerZoneSection key={`danger-${project.id}`} project={project} />
          )}
        </>
      )}
    </section>
  );
}
